import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Waves,
  UploadSimple,
  Trash,
  Plus,
  Timer,
  Check,
  X as XIcon,
  ArrowDown,
  ArrowUp,
  Warning,
  CloudCheck,
  CloudArrowUp,
  FileXls,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ocrWavePlan, importWavePlanExcel, fetchWavePlanToday, saveWavePlanToday } from "@/lib/api";
import useAutoRefresh from "@/lib/useAutoRefresh";

const DEFAULT_WAVES = ["12:01", "12:21"];
const AUTOSAVE_DEBOUNCE_MS = 700;
const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
  `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const minutesOfHHMM = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t || "");
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

const findClosestWave = (rowTime, waves) => {
  const rm = minutesOfHHMM(rowTime);
  if (rm === null || waves.length === 0) return null;
  let best = null;
  let bestDiff = Infinity;
  for (const w of waves) {
    const wm = minutesOfHHMM(w);
    if (wm === null) continue;
    const diff = Math.abs(wm - rm);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = w;
    }
  }
  return best;
};

const diffMinutes = (rowTime, waveTime) => {
  const rm = minutesOfHHMM(rowTime);
  const wm = minutesOfHHMM(waveTime);
  if (rm === null || wm === null) return null;
  return rm - wm;
};

export default function PlanVaguePage() {
  const [waves, setWaves] = useState(DEFAULT_WAVES);
  const [newWave, setNewWave] = useState("");
  const [rows, setRows] = useState([]); // { id, truck_code, driver_name, time, present }
  const [uploading, setUploading] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [saveState, setSaveState] = useState({ status: "idle", at: null }); // idle | saving | saved | error
  const [today, setToday] = useState("");
  const fileRef = useRef(null);
  const excelFileRef = useRef(null);
  const saveTimer = useRef(null);
  const lastServerPayload = useRef("");
  const lastUnmatched = useRef("");

  const load = async ({ silent = false } = {}) => {
    try {
      const data = await fetchWavePlanToday();
      setToday(data.date || "");
      // Only overwrite local state if server payload actually differs — avoids
      // wiping the user's in-flight edit during a background refresh.
      const serverPayload = JSON.stringify({ waves: data.waves || [], rows: data.rows || [] });
      if (serverPayload !== lastServerPayload.current) {
        lastServerPayload.current = serverPayload;
        const incomingWaves = (data.waves && data.waves.length ? data.waves : DEFAULT_WAVES).slice();
        setWaves(incomingWaves);
        const incomingRows = (data.rows || []).map((r) => ({
          id: r.id || uid(),
          truck_code: r.truck_code || "",
          driver_name: r.driver_name || "",
          time: r.time || "",
          present: !!r.present,
        }));
        setRows(incomingRows);
      }
      setHydrated(true);
    } catch (e) {
      if (!silent) toast.error("Impossible de charger le plan partagé");
      setHydrated(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useAutoRefresh(() => load({ silent: true }), 15000);

  // Debounced auto-save whenever waves or rows change (after initial hydration)
  useEffect(() => {
    if (!hydrated) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = { waves, rows };
      const payloadStr = JSON.stringify(payload);
      if (payloadStr === lastServerPayload.current) return;
      setSaveState({ status: "saving", at: null });
      try {
        const res = await saveWavePlanToday(payload);
        lastServerPayload.current = payloadStr;
        setSaveState({ status: "saved", at: res.updated_at || new Date().toISOString() });
        const missing = (res.unmatched_drivers || []).join(", ");
        if (missing && missing !== lastUnmatched.current) {
          toast.warning(`${res.unmatched_drivers.length} chauffeur(s) du plan sans attribution DLP4 : ${missing}`);
        }
        lastUnmatched.current = missing;
      } catch {
        setSaveState({ status: "error", at: null });
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [waves, rows, hydrated]);

  const sortedWaves = useMemo(
    () => [...waves].filter(Boolean).sort((a, b) => minutesOfHHMM(a) - minutesOfHHMM(b)),
    [waves]
  );

  const addWave = () => {
    const m = /^(\d{1,2}):?(\d{2})$/.exec(newWave.trim());
    if (!m) {
      toast.error("Format d'heure invalide (HH:MM)");
      return;
    }
    const hh = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    if (hh > 23 || mm > 59) {
      toast.error("Heure invalide");
      return;
    }
    const val = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    if (waves.includes(val)) {
      toast.info("Cette vague existe déjà");
      return;
    }
    setWaves([...waves, val]);
    setNewWave("");
  };

  const removeWave = (w) => setWaves(waves.filter((x) => x !== w));

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      toast.error("Sélectionnez au moins une image");
      return;
    }
    setUploading(true);
    let totalRows = 0;
    try {
      const results = await Promise.all(files.map((f) => ocrWavePlan(f).catch((e) => ({ error: e }))));
      const merged = [];
      for (const res of results) {
        if (res?.error) {
          toast.error(res.error?.response?.data?.detail || "OCR échoué sur une image");
          continue;
        }
        for (const r of res.rows || []) {
          merged.push({
            id: uid(),
            truck_code: r.truck_code || "",
            driver_name: r.driver_name || "",
            time: r.time || "",
            present: false,
          });
        }
      }
      totalRows = merged.length;
      // Merge into current rows; deduplicate by (truck_code + driver_name + time)
      setRows((prev) => {
        const key = (r) => `${r.truck_code}|${r.driver_name}|${r.time}`;
        const seen = new Set(prev.map(key));
        const additions = merged.filter((r) => !seen.has(key(r)));
        return [...prev, ...additions];
      });
      toast.success(`${totalRows} ligne(s) extraite(s)`);
    } catch (e) {
      toast.error("Erreur d'extraction");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Import from the Amazon Cortex Excel export (one click, 100% reliable)
  const handleExcel = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) {
      toast.error("Sélectionnez un fichier Excel");
      return;
    }
    const name = (file.name || "").toLowerCase();
    if (!(name.endsWith(".xlsx") || name.endsWith(".xlsm"))) {
      toast.error("Fichier .xlsx attendu (export Cortex)");
      return;
    }
    setImportingExcel(true);
    try {
      const res = await importWavePlanExcel(file);
      const incoming = (res?.rows || []).map((r) => ({
        id: uid(),
        truck_code: r.truck_code || "",
        driver_name: r.driver_name || "",
        time: r.time || "",
        present: false,
      }));
      // Add any new waves detected in the file
      if (Array.isArray(res?.waves) && res.waves.length > 0) {
        setWaves((prev) => Array.from(new Set([...prev, ...res.waves])).sort());
      }
      setRows((prev) => {
        const key = (r) => `${r.truck_code}|${r.driver_name}|${r.time}`;
        const seen = new Set(prev.map(key));
        const additions = incoming.filter((r) => !seen.has(key(r)));
        return [...prev, ...additions];
      });
      toast.success(
        `${incoming.length} ligne(s) importée(s) depuis « ${file.name} »` +
          (res?.sheet ? ` · feuille : ${res.sheet}` : "")
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import Excel impossible");
    } finally {
      setImportingExcel(false);
      if (excelFileRef.current) excelFileRef.current.value = "";
    }
  };

  const togglePresent = (id) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, present: !r.present } : r)));

  const updateRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  const addManualRow = (waveTime = "") => {
    setRows((prev) => [
      ...prev,
      { id: uid(), truck_code: "", driver_name: "", time: waveTime, present: false },
    ]);
  };

  const clearAll = () => {
    if (rows.length === 0) return;
    if (!window.confirm("Vider toute la liste ?")) return;
    setRows([]);
  };

  const setAllPresent = (val) => setRows((prev) => prev.map((r) => ({ ...r, present: val })));

  const grouped = useMemo(() => {
    const map = new Map();
    for (const w of sortedWaves) map.set(w, []);
    const orphans = [];
    for (const r of rows) {
      const w = findClosestWave(r.time, sortedWaves);
      if (w) map.get(w).push(r);
      else orphans.push(r);
    }
    // Sort each wave list alphabetically by driver name (case-insensitive, French locale).
    // Rows with no driver name fall to the end.
    const byNameAsc = (a, b) => {
      const an = (a.driver_name || "").trim();
      const bn = (b.driver_name || "").trim();
      if (!an && !bn) return 0;
      if (!an) return 1;
      if (!bn) return -1;
      return an.localeCompare(bn, "fr", { sensitivity: "base" });
    };
    for (const list of map.values()) list.sort(byNameAsc);
    orphans.sort(byNameAsc);
    return { map, orphans };
  }, [rows, sortedWaves]);

  const totalPresent = rows.filter((r) => r.present).length;

  // Detect trucks assigned to more than one driver (same truck_code appearing on
  // multiple rows). Empty codes are ignored. Comparison is case-insensitive.
  const duplicateCodes = useMemo(() => {
    const byCode = new Map();
    for (const r of rows) {
      const key = (r.truck_code || "").trim().toUpperCase();
      if (!key) continue;
      byCode.set(key, (byCode.get(key) || 0) + 1);
    }
    return new Set([...byCode.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [rows]);
  const isDuplicate = (code) =>
    duplicateCodes.has((code || "").trim().toUpperCase());

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-8 py-6" data-testid="plan-vague-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tag mb-1">NY TRANS</div>
          <h1 className="text-4xl md:text-5xl serif-display flex items-center gap-3">
            <Waves size={38} weight="duotone" className="text-blue-700" />
            Plan de vagues
          </h1>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">
            Partagé pour tous · Réinitialisé chaque jour {today && `· ${today}`}
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-widest font-bold" data-testid="wave-sync-state">
          {saveState.status === "saving" && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <CloudArrowUp size={13} weight="fill" /> Sauvegarde…
            </span>
          )}
          {saveState.status === "saved" && (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CloudCheck size={13} weight="fill" /> Sauvegardé
            </span>
          )}
          {saveState.status === "error" && (
            <span className="inline-flex items-center gap-1 text-red-600">
              <Warning size={13} weight="fill" /> Erreur de sauvegarde
            </span>
          )}
          {saveState.status === "idle" && hydrated && (
            <span className="inline-flex items-center gap-1 text-zinc-400">
              <CloudCheck size={13} weight="regular" /> Synchronisé
            </span>
          )}
        </div>
      </div>

      {/* Waves config */}
      <div className="border-2 border-zinc-900 bg-white p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-tag inline-flex items-center gap-2">
            <Timer size={14} weight="bold" /> Vagues définies
          </div>
          <div className="text-[11px] text-zinc-500 uppercase tracking-widest">
            {sortedWaves.length} vague(s)
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {sortedWaves.map((w, i) => (
            <div
              key={w}
              className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-900 bg-zinc-50"
              data-testid={`wave-chip-${w}`}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Vague {i + 1}
              </span>
              <span className="font-bold mono text-sm">{w}</span>
              <button
                onClick={() => removeWave(w)}
                className="text-zinc-400 hover:text-red-600"
                title="Supprimer cette vague"
                data-testid={`wave-remove-${w}`}
              >
                <XIcon size={12} weight="bold" />
              </button>
            </div>
          ))}
          {sortedWaves.length === 0 && (
            <span className="text-xs text-zinc-500 italic">Aucune vague — ajoutez-en une.</span>
          )}
        </div>

        <div className="flex items-end gap-2">
          <div>
            <Label className="label-tag">Nouvelle heure de vague</Label>
            <Input
              type="time"
              value={newWave}
              onChange={(e) => setNewWave(e.target.value)}
              className="rounded-none border-zinc-900 mono w-40"
              data-testid="wave-input"
            />
          </div>
          <Button
            onClick={addWave}
            className="rounded-none bg-zinc-900 hover:bg-zinc-700 h-10 gap-1"
            data-testid="wave-add"
          >
            <Plus size={14} weight="bold" /> Ajouter
          </Button>
        </div>
      </div>

      {/* Duplicate-route alert */}
      {duplicateCodes.size > 0 && (
        <div
          className="border-2 border-red-500 bg-red-50 px-4 py-3 mb-4 flex items-start gap-3"
          data-testid="wave-duplicates-alert"
        >
          <Warning size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-widest text-red-700">
              Attention · {duplicateCodes.size === 1 ? "2 chauffeurs sur le même numéro de route" : `${duplicateCodes.size} numéros de route dupliqués`}
            </div>
            <div className="mt-1 text-xs text-red-800 flex flex-wrap gap-1">
              {[...duplicateCodes].map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center px-2 py-0.5 bg-red-600 text-white font-bold mono text-[10px]"
                  data-testid={`wave-duplicate-code-${c}`}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upload */}
      <div className="border-2 border-zinc-900 bg-white p-4 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="label-tag inline-flex items-center gap-2">
            <UploadSimple size={14} weight="bold" /> Import du plan
          </div>
          {rows.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-zinc-500 uppercase tracking-widest">
                {rows.length} ligne(s) · {totalPresent} présent(s)
              </span>
              <button
                onClick={() => setAllPresent(true)}
                className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="wave-mark-all-present"
              >
                Tout cocher
              </button>
              <button
                onClick={() => setAllPresent(false)}
                className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-400 text-zinc-600 hover:bg-zinc-50"
                data-testid="wave-uncheck-all"
              >
                Tout décocher
              </button>
              <button
                onClick={clearAll}
                className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-red-500 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
                data-testid="wave-clear-all"
              >
                <Trash size={11} weight="bold" /> Vider
              </button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          {/* Excel import — recommended, fast & lossless */}
          <label
            className={`relative block border-2 border-dashed p-6 text-center cursor-pointer ${
              importingExcel
                ? "border-emerald-300 bg-emerald-50/60 opacity-70"
                : "border-emerald-600 bg-emerald-50/40 hover:border-emerald-700 hover:bg-emerald-50"
            }`}
            data-testid="wave-excel-dropzone"
          >
            <span className="absolute -top-2 left-3 bg-emerald-600 text-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest">
              Recommandé
            </span>
            <input
              ref={excelFileRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={importingExcel}
              onChange={(e) => handleExcel(e.target.files)}
              className="hidden"
              data-testid="wave-excel-input"
            />
            <FileXls size={30} weight="duotone" className="mx-auto mb-1 text-emerald-700" />
            <div className="text-sm font-bold uppercase tracking-widest text-emerald-900">
              {importingExcel ? "Import en cours…" : "Importer l'Excel Cortex"}
            </div>
            <div className="text-[11px] text-emerald-800/80 mt-1">
              .xlsx · Un seul clic · 100% fiable, pas d&apos;OCR
            </div>
          </label>

          {/* Screenshot OCR fallback */}
          <label
            className={`block border-2 border-dashed p-6 text-center cursor-pointer ${
              uploading
                ? "border-zinc-300 bg-zinc-50 opacity-70"
                : "border-zinc-400 hover:border-zinc-900 hover:bg-zinc-50"
            }`}
            data-testid="wave-upload-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              data-testid="wave-upload-input"
            />
            <UploadSimple size={28} weight="duotone" className="mx-auto mb-1 text-zinc-700" />
            <div className="text-sm font-bold uppercase tracking-widest">
              {uploading ? "Analyse en cours…" : "Ou importer des captures"}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              PNG / JPG · OCR (fallback si pas d&apos;Excel)
            </div>
          </label>
        </div>
      </div>

      {/* Waves display */}
      {sortedWaves.map((w, idx) => {
        const list = grouped.map.get(w) || [];
        const present = list.filter((r) => r.present).length;
        return (
          <div
            key={w}
            className="border-2 border-zinc-900 bg-white mb-4"
            data-testid={`wave-block-${w}`}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 text-white">
              <div className="inline-flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest opacity-70">
                  Vague {idx + 1}
                </span>
                <span className="serif-display text-2xl">{w}</span>
              </div>
              <div className="text-xs uppercase tracking-widest">
                {present}/{list.length} présent(s)
              </div>
            </div>
            {list.length === 0 ? (
              <div className="p-4 text-xs text-zinc-500 italic">Aucun chauffeur.</div>
            ) : (
              <ul className="divide-y divide-zinc-200">
                {list.map((r) => {
                  const delta = diffMinutes(r.time, w);
                  const late = delta !== null && delta > 0;
                  const dup = isDuplicate(r.truck_code);
                  return (
                    <li
                      key={r.id}
                      className={`flex items-center gap-2 px-3 py-2 ${
                        dup ? "bg-red-50" : r.present ? "bg-emerald-50" : ""
                      } ${dup ? "border-l-4 border-red-500" : ""}`}
                      data-testid={`wave-row-${r.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={r.present}
                        onChange={() => togglePresent(r.id)}
                        className="w-5 h-5 shrink-0 accent-emerald-600"
                        data-testid={`wave-present-${r.id}`}
                      />
                      <input
                        type="text"
                        value={r.truck_code}
                        onChange={(e) => updateRow(r.id, { truck_code: e.target.value.toUpperCase() })}
                        placeholder="CODE"
                        className={`font-bold mono text-sm w-24 shrink-0 border ${
                          dup ? "border-red-500 bg-red-50 text-red-700" : "border-transparent hover:border-zinc-300"
                        } focus:border-zinc-900 bg-transparent px-1 py-0.5 outline-none uppercase`}
                        data-testid={`wave-truck-${r.id}`}
                      />
                      <input
                        type="text"
                        value={r.driver_name}
                        onChange={(e) => updateRow(r.id, { driver_name: e.target.value })}
                        placeholder="Nom du chauffeur"
                        className="text-sm flex-1 min-w-0 border border-transparent hover:border-zinc-300 focus:border-zinc-900 bg-transparent px-1 py-0.5 outline-none"
                        data-testid={`wave-driver-${r.id}`}
                      />
                      <input
                        type="time"
                        value={r.time}
                        onChange={(e) => updateRow(r.id, { time: e.target.value })}
                        className="text-xs mono w-20 shrink-0 border border-transparent hover:border-zinc-300 focus:border-zinc-900 bg-transparent px-1 py-0.5 outline-none text-zinc-600"
                        data-testid={`wave-time-${r.id}`}
                      />
                      {delta !== null && delta !== 0 && (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                            late ? "bg-orange-500 text-white" : "bg-blue-500 text-white"
                          }`}
                        >
                          {late ? <ArrowDown size={10} weight="bold" /> : <ArrowUp size={10} weight="bold" />}
                          {late ? `+${delta}` : delta}m
                        </span>
                      )}
                      {dup && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white"
                          title="Ce numéro de route apparaît sur plusieurs lignes"
                          data-testid={`wave-dup-badge-${r.id}`}
                        >
                          <Warning size={10} weight="fill" /> Doublon
                        </span>
                      )}
                      <button
                        onClick={() => removeRow(r.id)}
                        className="text-zinc-300 hover:text-red-600 shrink-0"
                        title="Retirer cette ligne"
                        data-testid={`wave-row-remove-${r.id}`}
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <button
              onClick={() => addManualRow(w)}
              className="w-full px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 border-t border-zinc-200 inline-flex items-center justify-center gap-1"
              data-testid={`wave-add-row-${w}`}
            >
              <Plus size={11} weight="bold" /> Ajouter un chauffeur à cette vague
            </button>
          </div>
        );
      })}

      {/* Orphans (no wave matched — e.g. no time detected) */}
      {grouped.orphans.length > 0 && (
        <div
          className="border-2 border-amber-500 bg-white mb-4"
          data-testid="wave-block-orphans"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-amber-500 text-white">
            <div className="inline-flex items-center gap-2">
              <Warning size={16} weight="fill" />
              <span className="text-xs uppercase tracking-widest">
                Non affecté (heure introuvable)
              </span>
            </div>
            <div className="text-xs uppercase tracking-widest">
              {grouped.orphans.length} ligne(s)
            </div>
          </div>
          <ul className="divide-y divide-zinc-200">
            {grouped.orphans.map((r) => {
              const dup = isDuplicate(r.truck_code);
              return (
              <li
                key={r.id}
                className={`flex items-center gap-2 px-3 py-2 ${
                  dup ? "bg-red-50 border-l-4 border-red-500" : r.present ? "bg-emerald-50" : ""
                }`}
                data-testid={`wave-orphan-${r.id}`}
              >
                <input
                  type="checkbox"
                  checked={r.present}
                  onChange={() => togglePresent(r.id)}
                  className="w-5 h-5 shrink-0 accent-emerald-600"
                  data-testid={`wave-present-${r.id}`}
                />
                <input
                  type="text"
                  value={r.truck_code}
                  onChange={(e) => updateRow(r.id, { truck_code: e.target.value.toUpperCase() })}
                  placeholder="CODE"
                  className={`font-bold mono text-sm w-24 shrink-0 border ${
                    dup ? "border-red-500 bg-red-50 text-red-700" : "border-transparent hover:border-zinc-300"
                  } focus:border-zinc-900 bg-transparent px-1 py-0.5 outline-none uppercase`}
                  data-testid={`wave-truck-${r.id}`}
                />
                <input
                  type="text"
                  value={r.driver_name}
                  onChange={(e) => updateRow(r.id, { driver_name: e.target.value })}
                  placeholder="Nom du chauffeur"
                  className="text-sm flex-1 min-w-0 border border-transparent hover:border-zinc-300 focus:border-zinc-900 bg-transparent px-1 py-0.5 outline-none"
                  data-testid={`wave-driver-${r.id}`}
                />
                <input
                  type="time"
                  value={r.time}
                  onChange={(e) => updateRow(r.id, { time: e.target.value })}
                  className="text-xs mono w-20 shrink-0 border border-transparent hover:border-zinc-300 focus:border-zinc-900 bg-transparent px-1 py-0.5 outline-none text-zinc-600"
                  data-testid={`wave-time-${r.id}`}
                />
                {dup && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white"
                    title="Ce numéro de route apparaît sur plusieurs lignes"
                    data-testid={`wave-dup-badge-${r.id}`}
                  >
                    <Warning size={10} weight="fill" /> Doublon
                  </span>
                )}
                <button
                  onClick={() => removeRow(r.id)}
                  className="text-zinc-300 hover:text-red-600 shrink-0"
                  data-testid={`wave-row-remove-${r.id}`}
                >
                  <Trash size={14} weight="bold" />
                </button>
              </li>
              );
            })}
          </ul>
          <button
            onClick={() => addManualRow("")}
            className="w-full px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900 border-t border-zinc-200 inline-flex items-center justify-center gap-1"
            data-testid="wave-add-row-orphan"
          >
            <Plus size={11} weight="bold" /> Ajouter une ligne sans heure
          </button>
        </div>
      )}

      {rows.length === 0 && (
        <div className="border-2 border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500">
          Importez une capture pour commencer.
        </div>
      )}

      <div className="text-[10px] text-zinc-400 mt-4 flex items-center gap-1 justify-end">
        <Check size={10} weight="bold" /> Plan partagé — visible par tous et effacé au changement de jour.
      </div>
    </div>
  );
}
